/**
 * @file `useGameStore` — Pinia store driving the active game session: fetch / create /
 * progress / end-game, plus derived state (team score, current challenge, win condition).
 * Wraps `gameService` with offline-resilient happy paths; on Firestore failure every action
 * falls back to the IndexedDB-cached game so the player can keep playing.
 *
 * Persisted across reloads via `pinia-plugin-persistedstate`. UI components must mutate the
 * game only through these actions — direct Firestore writes from views are forbidden.
 */

import { defineStore } from 'pinia'
import { type Challenge, type Game, type Player } from '@/types/Types'
import {
  getGame,
  createGame,
  updateGame,
  removeGameFromOwner,
  deleteGame
} from '@/stores/gameService'
import { assignGameToUser } from '@/stores/users'
import { useAuthStore } from '@/stores/authStore'
import { BASE_SCORE_NEEDED_BY_TEAMMATE } from '@/types/Constants'
import { type Router } from 'vue-router'

/**
 * Firestore signals genuine offline / network-partition state with `code === 'unavailable'`.
 * Every other error (permission-denied, failed-precondition, quota exceeded, schema bug, …)
 * is unexpected and must surface in logs so it does not silently rot behind the offline path.
 */
const FIRESTORE_OFFLINE_CODE = 'unavailable'

function isOfflineError(e: unknown): boolean {
  return (
    typeof e === 'object' &&
    e !== null &&
    'code' in e &&
    (e as { code?: unknown }).code === FIRESTORE_OFFLINE_CODE
  )
}

function logUnexpectedError(action: string, e: unknown): void {
  if (isOfflineError(e)) return
  console.error(`[gameStore.${action}]`, e)
}

export const useGameStore = defineStore('game', {
  state: (): GameState => ({
    game: undefined,
    challenges: undefined,
    currentChallenge: undefined
  }),
  persist: true,

  getters: {
    /** Sum of every player's score. Returns 0 when no game is loaded (safe for SSR/template guards). */
    teamScore: (state: GameState) => {
      if (!state.game) return 0
      return calculateTeamScore(state.game.players)
    }
  },

  actions: {
    /** Loads an existing game by id. Tries Firestore first; falls back to the offline cache; sets `game` to `undefined` if both fail (caller decides recovery). */
    fetchGame: async (state: GameState, gameId: string, router: Router) => {
      try {
        state.game = await getGame(gameId, router)
      } catch (e) {
        logUnexpectedError('fetchGame', e)
        const { loadGameState } = await import('@/utils/offlineGame')
        const offlineGame = await loadGameState(gameId)
        if (offlineGame) {
          state.game = offlineGame
        } else {
          state.game = undefined
        }
      }
    },
    /**
     * Creates a new game in Firestore, attaches it to the signed-in user, then re-loads it
     * to populate `state.game`. Offline path: writes to IndexedDB only and synthesises a
     * temporary `offline-{timestamp}` id so the session can still start. Returns the id used.
     */
    startGame: async (state: GameState, game: Game, router: Router) => {
      const authStore = useAuthStore()

      let gameId: string | undefined
      try {
        gameId = await createGame(game)
        if (authStore.firebaseUser) {
          await assignGameToUser(authStore.firebaseUser.uid, gameId)
        }
        try {
          state.game = await getGame(gameId, router)
        } catch (e) {
          logUnexpectedError('startGame.getGame', e)
          const { loadGameState } = await import('@/utils/offlineGame')
          const offlineGame = await loadGameState(gameId)
          if (offlineGame) {
            state.game = offlineGame
          } else {
            state.game = undefined
          }
        }
        return gameId
      } catch (e) {
        logUnexpectedError('startGame.createGame', e)
        const { saveGameState, loadGameState } = await import('@/utils/offlineGame')
        await saveGameState(game)
        gameId = game.id || 'offline-' + Date.now()
        const offlineGame = await loadGameState(gameId)
        if (offlineGame) {
          state.game = offlineGame
        } else {
          state.game = game
        }
        return gameId
      }
    },
    /** Persists turn progression (challenge index + player scores) and re-pulls the canonical game state. Offline path only saves locally. */
    progressGame: async (state: GameState, router: Router) => {
      if (!state.game?.id) return
      try {
        await updateGame(state.game)
        state.game = await getGame(state.game.id, router)
      } catch (e) {
        logUnexpectedError('progressGame', e)
        const { saveGameState, loadGameState } = await import('@/utils/offlineGame')
        await saveGameState(state.game)
        if (typeof state.game.id === 'string') {
          const offlineGame = await loadGameState(state.game.id)
          if (offlineGame) {
            state.game = offlineGame
          }
        }
      }
    },
    /**
     * Archives the game to the owner's user document, then hard-deletes the game document.
     * On failure (typically offline) just clears the local cache so the UI exits the game
     * flow gracefully — the Cloud Functions scheduled cleanup will eventually reap the
     * orphan Firestore document.
     *
     * Either way the session state (`game`, `challenges`, `currentChallenge`) is cleared in
     * `finally`: the store is persisted across reloads, and a stale `currentChallenge` left
     * behind would keep in-game UI (e.g. the "This challenge" SideMenu group) visible after
     * the game has ended.
     */
    endGame: async (state: GameState) => {
      if (!state.game?.id) return
      try {
        await removeGameFromOwner(state.game)
        await deleteGame(state.game.id)
      } catch (e) {
        logUnexpectedError('endGame', e)
        const { clearGameState } = await import('@/utils/offlineGame')
        if (typeof state.game.id === 'string') {
          await clearGameState(state.game.id)
        }
      } finally {
        state.game = undefined
        state.challenges = undefined
        state.currentChallenge = undefined
      }
    },
    /**
     * Clears the local/persisted session without touching Firestore. Use on the
     * abandon path (user navigates away from an in-progress game) — unlike
     * `endGame`, it neither archives nor deletes the remote document. No-op when
     * no game is loaded.
     */
    clearGame: (state: GameState) => {
      if (!state.game?.id) return

      state.game = undefined
      state.challenges = undefined
      state.currentChallenge = undefined
    },
    /** Returns true iff the team's combined score meets `players.length * BASE_SCORE_NEEDED_BY_TEAMMATE`. Used by `Game.vue` to short-circuit the turn loop. */
    checkIfTeamWon: (state: GameState) => {
      if (!state.game) return false
      const neededscore = state.game.players.length * BASE_SCORE_NEEDED_BY_TEAMMATE

      return calculateTeamScore(state.game.players) >= neededscore
    },
    /** Syncs `currentChallenge` from `challenges[currentChallengeIndex]`. Call after `progressGame` so the in-game view reflects the new challenge. */
    getCurrentChallenge: (state: GameState) => {
      if (!state.challenges || !state.game) return
      state.currentChallenge = state.challenges[state.game.currentChallengeIndex]
    }
  }
})

interface GameState {
  game: Game | undefined
  challenges: Challenge[] | undefined
  currentChallenge: Challenge | undefined
}

/** Sum-reduce helper — pulled out so both the `teamScore` getter and `checkIfTeamWon` action stay symmetrical. */
function calculateTeamScore(players: Player[]) {
  return players.reduce((acc: number, player: Player) => acc + player.score, 0)
}
